import { Skeleton } from '@/components/ui/skeleton'
import type { ActivityFeedItem } from '@/src/types/admin/analytics'
import { UserPlus, DollarSign, CheckCircle, XCircle } from 'lucide-react'

const typeConfig = {
  new_customer: {
    icon: UserPlus,
    badgeVariant: 'secondary' as const,
    label: 'ลูกค้าใหม่',
    color: 'text-blue-500',
  },
  large_transaction: {
    icon: DollarSign,
    badgeVariant: 'secondary' as const,
    label: 'ธุรกรรมใหญ่',
    color: 'text-amber-500',
  },
  payment_approved: {
    icon: CheckCircle,
    badgeVariant: 'secondary' as const,
    label: 'อนุมัติแล้ว',
    color: 'text-green-500',
  },
  payment_rejected: {
    icon: XCircle,
    badgeVariant: 'destructive' as const,
    label: 'ปฏิเสธ',
    color: 'text-red-500',
  },
}

function timeAgo(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'เพิ่งเมื่อกี้'
  if (mins < 60) return `${mins} นาทีที่แล้ว`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs} ชั่วโมงที่แล้ว`
  return `${Math.floor(hrs / 24)} วันที่แล้ว`
}

interface ActivityFeedProps {
  items: ActivityFeedItem[]
  maxItems?: number
  loading?: boolean
}

export function ActivityFeed({ items, maxItems = 20, loading = false }: ActivityFeedProps) {
  if (loading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="flex gap-3 items-start">
            <Skeleton className="h-8 w-8 rounded-full shrink-0" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  const displayed = items.slice(0, maxItems)

  if (displayed.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-8">ไม่มีกิจกรรมล่าสุด</p>
    )
  }

  return (
    <div className="overflow-y-auto max-h-64">
      <div className="space-y-3 pr-4">
        {displayed.map((item, idx) => {
          const cfg = typeConfig[item.type]
          const Icon = cfg.icon
          return (
            <div key={idx} className="flex gap-3 items-start">
              <div className={`mt-0.5 shrink-0 ${cfg.color}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium leading-snug truncate">{item.label}</p>
                <p className="text-xs text-muted-foreground truncate">{item.detail}</p>
                {item.meta && (
                  <p className="text-xs text-muted-foreground truncate">{item.meta}</p>
                )}
              </div>
              <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                {timeAgo(item.at)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
